import React, { useState, useRef, useEffect, useContext } from 'react';
import { Dropdown } from 'bootstrap';
import { AnalyticType } from '../../model/AnalyticType';
import { HistoricalIcon } from './HistoricalIcon';
import { PlusIcon } from '../icons/PlusIcon';
import { HistoricalOption, HistoricalService } from '../../model/Historical';
import { TrashIcon } from '../icons/TrashIcon';
import { AccountContext } from '../../contexts/AccountContext';
import { t } from '../../i18n';

const HistoricalMenu = ({ onChange }: Props) => {
  const [primaryOption, setPrimaryOption] = useState<HistoricalOption>(HistoricalService.getDefault());
  const [secondaryOption, setSecondaryOption] = useState('');
  const [hover, setHover] = useState(false);
  const accountParams = useContext(AccountContext);
  const primaryToggleRef = useRef<HTMLDivElement | null>(null);
  const secondaryToggleRef = useRef<HTMLDivElement | null>(null);

  const onSelectPrimaryOption = (option: HistoricalOption) => {
    setPrimaryOption(option);
    setSecondaryOption('');
    onChange(option.type);
    primaryToggleRef.current && Dropdown.getInstance(primaryToggleRef.current)?.hide();
  }

  const onSelectSecondaryOption = (option: string) => {
    setSecondaryOption(option);
    onChange(primaryOption.type, option as AnalyticType);
    secondaryToggleRef.current && Dropdown.getInstance(secondaryToggleRef.current)?.hide();
  }

  const onTogglePrimaryOption = () => {
    primaryToggleRef.current && Dropdown.getOrCreateInstance(primaryToggleRef.current).toggle();
  }

  const onToggleSecondaryOption = () => {
    secondaryToggleRef.current && Dropdown.getOrCreateInstance(secondaryToggleRef.current).toggle();
  }

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      for(const toggleRef of [ primaryToggleRef, secondaryToggleRef ]) {
        const toggle = toggleRef.current;
        if(!toggle || toggle.getAttribute('aria-expanded') !== 'true' || toggle.contains(target)) {
          continue;
        }
        const menu = toggle.nextElementSibling;
        if(menu && menu.contains(target)) {
          continue;
        }
        Dropdown.getInstance(toggle)?.hide();
      }
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, []);

  const onDeleteSecondaryOption = () => {
    setSecondaryOption('');
    setHover(false);
    onChange(primaryOption.type);
  }

  const optionChangedRef = (element: any) => {
    if(element) {
      element.onmouseenter = () => {
        setHover(true);
      };
      element.onmouseleave = () => {
        setHover(false);
      };
    }
  }

  return (
    <div className="d-flex align-items-center justify-content-end justify-content-lg-start">
      <div ref={primaryToggleRef} className="rounded bg-label-secondary d-inline-flex align-items-center flex-column historical-option" data-bs-toggle="dropdown" aria-expanded="false" onClick={onTogglePrimaryOption}>
        <HistoricalIcon type={primaryOption.type} />
        <span className="badge text-secondary text-capitalize">{t(`historical.analyticTypes.${primaryOption.type}`)}</span>
      </div>
      <ul className="dropdown-menu">
        {
          HistoricalService.getAll().map(option =>
            option.type !== AnalyticType.sales || accountParams.modules?.includes('sales') ?
              <li key={option.type}>
                <span className={`dropdown-item text-capitalize ${option.type === primaryOption.type ? 'active' : ''}`} onClick={() => onSelectPrimaryOption(option)}>{t(`historical.analyticTypes.${option.type}`)}</span>
              </li> : ''
          )
        }
      </ul>
      {
        secondaryOption ? 
          <div ref={optionChangedRef} className="position-relative rounded bg-label-secondary d-inline-flex align-items-center flex-column ms-4 historical-option">
            <HistoricalIcon type={secondaryOption as AnalyticType} />
            <span className="badge text-secondary text-capitalize">{t(`historical.analyticTypes.${secondaryOption}`)}</span>
            <div className={`position-absolute rounded bg-dark bg-opacity-50 w-100 h-100 top-0 start-0 align-items-center justify-content-center ${hover ? 'd-flex' : 'd-none'}`} onClick={onDeleteSecondaryOption}>
              <TrashIcon size='sm' />
            </div>
          </div> : primaryOption.related.length ?
          <div>
            <div ref={secondaryToggleRef} className="rounded ms-3 p-3 historical-plus" data-bs-toggle="dropdown" aria-expanded="false" onClick={onToggleSecondaryOption}>
              <span><PlusIcon /></span>
            </div>
            <ul className="dropdown-menu">
              {
                primaryOption.related.map(value =>
                  value !== AnalyticType.sales || accountParams.modules?.includes('sales') ?
                    <li key={value}>
                      <span className={`dropdown-item text-capitalize ${value === secondaryOption ? 'active' : ''}`} onClick={() => onSelectSecondaryOption(value)}>{t(`historical.analyticTypes.${value}`)}</span>
                    </li> : ''
                )
              }
            </ul>
          </div>
        : ''  
      }
    </div>
  );
}

interface Props {
  onChange: (primaryOption: AnalyticType, secondaryOption?: AnalyticType) => void
}

export { HistoricalMenu };